<template>
  <div>
    <h1 class="page-header mb-3">Product Customers</h1>
    <div class="row">
      <div class="col-xl-12">
        <div class="card border-0 mb-3 overflow-hidden bg-gray-800 text-white">
          <div class="card-body">
            <!-- Product Selector and Export Buttons Row -->
            <div class="mb-4 d-flex justify-content-between align-items-end">
              <div class="flex-grow-1 me-3">
                <label class="form-label">Select Product:</label>
                <select
                  v-model="selectedProduct"
                  class="form-select bg-gray-700 text-white border-gray-600"
                  @change="handleProductChange"
                >
                  <option value="">Select a product...</option>
                  <option
                    v-for="product in products"
                    :key="product.id"
                    :value="product.id"
                  >
                    {{ product.name }}
                  </option>
                </select>
              </div>

              <!-- Export Buttons -->
              <div class="d-flex gap-2" v-if="rows && rows.length">
                <button
                  @click="exportToExcel"
                  class="btn btn-success"
                  :disabled="loading"
                >
                  <i class="fa fa-file-excel me-1"></i> Export to Excel
                </button>
                <button
                  @click="exportToCSV"
                  class="btn btn-info"
                  :disabled="loading"
                >
                  <i class="fa fa-file-csv me-1"></i> Export to CSV
                </button>
                <button
                  @click="showEmailList"
                  class="btn btn-primary"
                  :disabled="loading"
                >
                  <i class="fa fa-envelope me-1"></i> Show Email List
                </button>
              </div>
            </div>

            <!-- Loading Spinner -->
            <div v-if="loading" class="loading text-center">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>

            <!-- No Product Selected Message -->
            <div v-if="!loading && !selectedProduct" class="alert alert-info">
              Please select a product to view customer data.
            </div>

            <!-- Error Message -->
            <div v-if="error" class="alert alert-danger">
              {{ error }}
            </div>

            <!-- Data Table -->
            <vue-good-table
              v-if="!loading && selectedProduct && rows && rows.length"
              :columns="columns"
              :rows="rows"
              :lineNumbers="false"
              :search-options="{
                enabled: true,
                skipDiacritics: true,
                placeholder: 'Search Current Table'
              }"
              :pagination-options="{
                enabled: true,
                position: 'bottom',
                perPage: 100,
                mode: 'pages'
              }"
              :sort-options="{
                enabled: true,
                multipleColumns: true,
                initialSortBy: [{field: 'purchase_date', type: 'desc'}]
              }"
            >
              <template #table-row="props">
                <span v-if="props.column.field === 'order_id'">
                  <a
                    :href="`https://www.cardsofyourdestiny.com/wp-admin/post.php?post=${props.row.order_id}&action=edit`"
                    target="_blank"
                    class="text-blue-500 hover:text-blue-700"
                  >
                    {{props.row.order_id}}
                  </a>
                </span>
                <span v-else>
                  {{props.formattedRow[props.column.field]}}
                </span>
              </template>
            </vue-good-table>

            <!-- No Results Message -->
            <div v-if="!loading && selectedProduct && (!rows || !rows.length)" class="alert alert-warning">
              No customers found for this product.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Email List Modal -->
    <b-modal
      id="emailListModal"
      ref="emailModal"
      title="Email List"
      modal-class="bg-gray-800"
      content-class="bg-gray-800"
      header-class="bg-gray-800 text-white border-gray-700"
      body-class="bg-gray-800 text-white"
      footer-class="bg-gray-800 text-white border-gray-700"
      hide-header-close
    >
      <template #default>
        <div class="form-group">
          <label class="mb-2 text-white">Email Addresses (Comma Separated)</label>
          <textarea
            ref="emailTextarea"
            class="form-control bg-gray-700 text-white border-gray-600"
            rows="10"
            readonly
            v-model="emailList"
          ></textarea>
        </div>
        <div class="mt-3 text-white-50">
          Total Emails: {{ emailCount }}
        </div>
      </template>

      <template #footer>
        <button
          type="button"
          class="btn btn-primary"
          @click="copyToClipboard"
        >
          <i class="fa fa-copy me-1"></i> Copy to Clipboard
        </button>
        <button
          type="button"
          class="btn btn-secondary ms-2"
          @click="hideModal"
        >
          Close
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { useCookies } from "vue3-cookies";
import axios from "axios";
import XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { BModal } from 'bootstrap-vue';

export default {
  name: 'customersproducts',
  components: {
    BModal
  },
  setup() {
    const { cookies } = useCookies();
    return { cookies };
  },
  data() {
    return {
      loading: false,
      error: null,
      selectedProduct: '',
      emailList: '',
      emailCount: 0,
      products: [
        { id: '19344', name: 'Book of Destiny 6.0 Reader Edition (PC and Mac)' },
        { id: '23433', name: 'Book of Destiny Reporter 6.0 (PC and Mac)' },
        { id: '22654', name: 'Love Cards Reporter 6.0 (PC and Mac)' },
        { id: '23721', name: 'Special! Destiny Software Bundle (PC and Mac)' },
        { id: '4878', name: 'Book of Destiny Pro and Love Cards Pro Online Bundle' },
        { id: '4819', name: 'Book of Destiny Application Pro Online' },
        { id: '4823', name: 'Love Cards Application Pro Online' },
          /*
        { id: '4881', name: 'Book of Destiny and Love Cards Bundle Online' },

        { id: '4882', name: 'Book of Destiny Application Online' },

        { id: '4824', name: 'Love Cards Application Online' },

           */
        { id: '77904', name: 'Time Cards Program (PC and Mac)' }
      ],
      columns: [{
        label: 'Customer Name',
        field: 'customer_name',
        thClass: 'text-nowrap',
        tdClass: 'text-nowrap'
      }, {
        label: 'Email',
        field: 'customer_email',
        thClass: 'text-nowrap',
        tdClass: 'text-nowrap'
      }, {
        label: 'Purchased',
        field: 'purchase_date',
        type: 'date',
        dateInputFormat: 'yyyy-MM-dd HH:mm:ss',
        dateOutputFormat: 'MMM dd, yyyy HH:mm',
        thClass: 'text-nowrap',
        tdClass: 'text-nowrap'
      }, {
        label: 'Total',
        field: 'order_total',
        type: 'number',
        thClass: 'text-nowrap',
        tdClass: 'text-nowrap',
        formatFn: (value) => `$${parseFloat(value).toFixed(2)}`
      }, {
        label: 'Order ID',
        field: 'order_id',
        thClass: 'text-nowrap',
        tdClass: 'text-nowrap'
      }],
      rows: null,
    }
  },
  methods: {
    handleProductChange() {
      if (this.selectedProduct) {
        this.fetchReportData();
      } else {
        this.rows = null;
      }
    },
    fetchReportData() {
      if (!this.selectedProduct) return;

      this.error = null;
      this.loading = true;
      this.rows = null;

      axios
        .post(`https://api.destinycards.com/v1/reportadmin/product/list/${this.selectedProduct}`)
        .then(response => {
          this.rows = response.data.data;
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
          this.error = 'Error loading customer data. Please try again.';
          console.error('Error fetching data:', error);
        });
    },
    formatDataForExport() {
      const selectedProduct = this.products.find(p => p.id === this.selectedProduct);
      const productName = selectedProduct ? selectedProduct.name.replace(/[^a-zA-Z0-9]/g, '_') : 'Unknown_Product';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

      return {
        data: this.rows.map(row => ({
          'Customer Name': row.customer_name || '',
          'Email': row.customer_email || '',
          'Purchase Date': new Date(row.purchase_date).toLocaleString(),
          'Total': `$${parseFloat(row.order_total || 0).toFixed(2)}`,
          'Order ID': (row.order_id || '').toString()
        })),
        filename: `${productName}-customers-${timestamp}`
      };
    },
    exportToExcel() {
      try {
        const { data, filename } = this.formatDataForExport();

        // Create workbook and worksheet
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Customers");

        // Save the file
        XLSX.writeFile(wb, `${filename}.xlsx`);
      } catch (error) {
        console.error('Export to Excel error:', error);
        this.error = 'Error exporting to Excel. Please try again.';
      }
    },
    exportToCSV() {
      try {
        const { data, filename } = this.formatDataForExport();

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(data);

        // Generate CSV content
        const csv = XLSX.utils.sheet_to_csv(ws);

        // Create blob and save
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        FileSaver.saveAs(blob, `${filename}.csv`);
      } catch (error) {
        console.error('Export to CSV error:', error);
        this.error = 'Error exporting to CSV. Please try again.';
      }
    },
    showEmailList() {
      if (!this.rows || !this.rows.length) return;

      // Create email list
      const emails = this.rows
        .map(row => row.customer_email)
        .filter(email => email && email.trim()) // Remove empty or null emails
        .sort()
        .join(', ');

      this.emailList = emails;
      this.emailCount = emails.split(',').length;

      // Show modal
      this.$refs.emailModal.show();
    },
    hideModal() {
      this.$refs.emailModal.hide();
    },
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.emailList);

        // Show success feedback
        const btn = event.target;
        const originalText = btn.innerHTML;
        btn.innerHTML = '<i class="fa fa-check me-1"></i> Copied!';
        btn.classList.add('btn-success');
        btn.classList.remove('btn-primary');

        setTimeout(() => {
          btn.innerHTML = originalText;
          btn.classList.remove('btn-success');
          btn.classList.add('btn-primary');
        }, 2000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
        this.error = 'Failed to copy to clipboard. Please try manually selecting the text.';
      }
    }
  }
}
</script>

<style>
tr:hover td {
  background-color: #3F51B5;
}

.vgt-table a {
  color: #60A5FA;
  text-decoration: none;
}

.vgt-table a:hover {
  color: #93C5FD;
  text-decoration: underline;
}

.form-select {
  display: block;
  width: 100%;
  padding: 0.375rem 2.25rem 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.25rem;
  transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;
}

.form-label {
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.btn {
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.25rem;
  transition: all .15s ease-in-out;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

.btn-success {
  color: #fff;
  background-color: #198754;
  border-color: #198754;
}

.btn-success:hover {
  background-color: #157347;
  border-color: #146c43;
}

.btn-info {
  color: #000;
  background-color: #0dcaf0;
  border-color: #0dcaf0;
}

.btn-info:hover {
  background-color: #31d2f2;
  border-color: #25cff2;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-3 {
  margin-right: 1rem;
}

.modal-content {
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-backdrop {
  opacity: 0.5;
}

.form-control:read-only {
  background-color: rgba(0, 0, 0, 0.1);
}

.form-control:read-only:focus {
  background-color: rgba(0, 0, 0, 0.2);
  box-shadow: none;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}
</style>
